/**
 * Organization Settings Page - Manage org users and modules
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import {
  Settings,
  Users,
  Boxes,
  Building2,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  ChevronLeft,
  Loader2,
  Shield,
  UserPlus,
  ToggleLeft,
  ToggleRight,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ORG_ROLES = [
  { value: "org_admin", label: "Org Admin", description: "Full access to org" },
  { value: "org_editor", label: "Org Editor", description: "Can edit data" },
  { value: "org_viewer", label: "Org Viewer", description: "View only access" }
];

export default function OrgSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  
  // Assign user dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({
    user_id: "",
    org_role: "org_viewer",
    is_default_org: false
  });
  const [assigning, setAssigning] = useState(false);
  
  // Remove user dialog
  const [removeDialog, setRemoveDialog] = useState({ open: false, user: null });
  
  // Module toggle loading
  const [togglingModule, setTogglingModule] = useState(null);
  
  useEffect(() => {
    loadData();
  }, [orgId]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load org details
      const orgRes = await api.get(`/orgs/${orgId}`);
      setOrg(orgRes.data.org || orgRes.data);
      
      // Load org users
      const usersRes = await api.get(`/orgs/${orgId}/users`);
      setUsers(usersRes.data.users || []);
      
      // Load modules
      const modulesRes = await api.get(`/orgs/${orgId}/modules`);
      setModules(modulesRes.data.modules || []);
      
      // Load all users for assignment dropdown
      try {
        const allUsersRes = await api.get(`/users`);
        setAllUsers(allUsersRes.data.users || []);
      } catch (e) {
        // Users endpoint might not be accessible
        setAllUsers([]);
      }
    } catch (error) {
      console.error("Failed to load org settings:", error);
      toast.error("Failed to load organization settings");
      navigate("/orgs");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAssignUser = async () => {
    if (!assignForm.user_id) {
      toast.error("Please select a user");
      return;
    }
    
    try {
      setAssigning(true);
      await api.post(`/orgs/${orgId}/users`, assignForm);
      toast.success("User assigned to organization");
      setShowAssignDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign user");
    } finally {
      setAssigning(false);
    }
  };
  
  const handleRemoveUser = async () => {
    if (!removeDialog.user) return;
    
    try {
      await api.delete(`/orgs/${orgId}/users/${removeDialog.user.user_id}`);
      toast.success("User removed from organization");
      loadData();
    } catch (error) {
      toast.error("Failed to remove user");
    } finally {
      setRemoveDialog({ open: false, user: null });
    }
  };
  
  const handleToggleModule = async (moduleType, currentEnabled) => {
    try {
      setTogglingModule(moduleType);
      await api.put(`/orgs/${orgId}/modules`, {
        module_type: moduleType,
        enabled: !currentEnabled
      });
      toast.success(`Module ${!currentEnabled ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle module");
    } finally {
      setTogglingModule(null);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };
  
  // Get available users (not already assigned)
  const availableUsers = allUsers.filter(u => 
    !users.some(assigned => assigned.user_id === u.id)
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="org-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/org/${orgId}`)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6 text-zinc-400" />
              Organization Settings
            </h1>
            <p className="text-zinc-400 text-sm">{org?.name}</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
          <TabsTrigger value="users" className="flex items-center gap-2 data-[state=active]:bg-zinc-800">
            <Users className="w-4 h-4" />
            Assign Users
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2 data-[state=active]:bg-zinc-800">
            <Boxes className="w-4 h-4" />
            Access Modules
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2 data-[state=active]:bg-zinc-800">
            <Building2 className="w-4 h-4" />
            General
          </TabsTrigger>
        </TabsList>
        
        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Assigned Users
                </CardTitle>
                <CardDescription>
                  Manage who has access to this organization
                </CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setAssignForm({ user_id: "", org_role: "org_viewer", is_default_org: false });
                  setShowAssignDialog(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign User
              </Button>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No users assigned</p>
                  <p className="text-sm">Assign users to give them access</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">User</TableHead>
                      <TableHead className="text-zinc-400">Org Role</TableHead>
                      <TableHead className="text-zinc-400">System Role</TableHead>
                      <TableHead className="text-zinc-400">Default Org</TableHead>
                      <TableHead className="text-zinc-400">Assigned</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id} className="border-zinc-800">
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.user_name}</div>
                            <div className="text-sm text-zinc-500">{user.user_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {ORG_ROLES.find(r => r.value === user.org_role)?.label || user.org_role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-zinc-400">{user.user_role}</span>
                        </TableCell>
                        <TableCell>
                          {user.is_default_org ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <span className="text-zinc-600">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-zinc-400">
                          {formatDate(user.assigned_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setRemoveDialog({ open: true, user })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Modules Tab */}
        <TabsContent value="modules">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Boxes className="w-5 h-5 text-purple-400" />
                Access Modules
              </CardTitle>
              <CardDescription>
                Enable or disable feature modules for this organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {modules.map((module) => (
                  <div 
                    key={module.module_type}
                    className={`p-4 rounded-lg border ${
                      module.enabled 
                        ? 'bg-zinc-800/50 border-zinc-700' 
                        : 'bg-zinc-900 border-zinc-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${
                          module.enabled ? 'bg-green-500/20' : 'bg-zinc-800'
                        }`}>
                          {module.module_type === 'ubm' && (
                            <Building2 className={`w-6 h-6 ${module.enabled ? 'text-green-400' : 'text-zinc-500'}`} />
                          )}
                          {module.module_type === 'constellation' && (
                            <Shield className={`w-6 h-6 ${module.enabled ? 'text-green-400' : 'text-zinc-500'}`} />
                          )}
                          {module.module_type === 'agent_studio' && (
                            <Settings className={`w-6 h-6 ${module.enabled ? 'text-green-400' : 'text-zinc-500'}`} />
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-lg">{module.name}</h3>
                          <p className="text-sm text-zinc-500">{module.description}</p>
                          {module.pages && (
                            <div className="flex gap-2 mt-2">
                              {module.pages.map(page => (
                                <Badge key={page} variant="secondary" className="text-xs">
                                  {page}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {module.enabled && module.enabled_at && (
                          <span className="text-xs text-zinc-500">
                            Enabled {formatDate(module.enabled_at)}
                          </span>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Switch
                                  checked={module.enabled}
                                  onCheckedChange={() => handleToggleModule(module.module_type, module.enabled)}
                                  disabled={togglingModule === module.module_type}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {module.enabled ? 'Disable module' : 'Enable module'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* General Tab */}
        <TabsContent value="general">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-zinc-400" />
                Organization Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-zinc-400">Name</Label>
                  <p className="text-lg">{org?.name}</p>
                </div>
                <div>
                  <Label className="text-zinc-400">Created</Label>
                  <p className="text-lg">{formatDate(org?.created_at)}</p>
                </div>
                {org?.description && (
                  <div className="col-span-2">
                    <Label className="text-zinc-400">Description</Label>
                    <p>{org.description}</p>
                  </div>
                )}
                {org?.address && (
                  <div className="col-span-2">
                    <Label className="text-zinc-400">Address</Label>
                    <p>
                      {org.address}
                      {org.city && `, ${org.city}`}
                      {org.state && `, ${org.state}`}
                      {org.postcode && ` ${org.postcode}`}
                    </p>
                  </div>
                )}
                {org?.contact_email && (
                  <div>
                    <Label className="text-zinc-400">Contact Email</Label>
                    <p>{org.contact_email}</p>
                  </div>
                )}
                {org?.contact_phone && (
                  <div>
                    <Label className="text-zinc-400">Contact Phone</Label>
                    <p>{org.contact_phone}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Assign User Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Assign User</DialogTitle>
            <DialogDescription>
              Add a user to this organization with a specific role
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>User</Label>
              <Select 
                value={assignForm.user_id} 
                onValueChange={(v) => setAssignForm({ ...assignForm, user_id: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {availableUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Role</Label>
              <Select 
                value={assignForm.org_role} 
                onValueChange={(v) => setAssignForm({ ...assignForm, org_role: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {ORG_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <div>{role.label}</div>
                        <div className="text-xs text-zinc-500">{role.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Set as Default Org</Label>
                <p className="text-xs text-zinc-500">User will land here after login</p>
              </div>
              <Switch
                checked={assignForm.is_default_org}
                onCheckedChange={(v) => setAssignForm({ ...assignForm, is_default_org: v })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignUser}
              disabled={assigning}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Remove User Dialog */}
      <AlertDialog open={removeDialog.open} onOpenChange={(open) => setRemoveDialog({ open, user: null })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{removeDialog.user?.user_name}" from this organization.
              They will lose access to all org data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
